import { useTranslation } from 'react-i18next';
import { LogoComponent } from '@/cleanapi/Logo.tsx';
import { useLocation, useNavigate } from 'react-router';
import { UserMenuComponent } from '@/cleanapi/UserMenuComponent.tsx';
import type { CurrentUserLoader } from '@/cleanapi/CurrentUser.ts';
import { IpponHSpace, IpponHSpaceSlot } from '@ippon-ui/react';
import { DiagnosticTrackingIndicatorComponent } from '@/diagnostic';
import { RuleExtractionTrackingIndicatorComponent } from '@/rules-management';

type Tab = {
  name: string;
  text: string;
  path: string;
};

const useCurrentTabNameFromUrl = (): string => {
  const location = useLocation();

  const pathname = location.pathname;

  if (pathname.startsWith('/rules')) {
    return 'rules';
  }
  if (pathname.startsWith('/history')) {
    return 'history';
  }
  return 'diagnostic';
};

const TabComponent = ({ tab, current }: { tab: Tab; current: string }) => {
  const navigate = useNavigate();
  const isCurrent = current === tab.name;
  const tabIndex = isCurrent ? -1 : undefined;

  return (
    <button
      key={tab.name}
      type="button"
      className="ippon-tab"
      role="tab"
      id={`tab-${tab.name}`}
      aria-controls="main-navigation"
      aria-selected={isCurrent}
      data-selector="cleanapi.menu.tab"
      onClick={() => navigate(tab.path)}
      tabIndex={tabIndex}
    >
      {tab.text}
    </button>
  );
};

export const HeaderComponent = ({
  currentUser,
  onRefreshCurrentUser,
}: {
  currentUser: CurrentUserLoader;
  onRefreshCurrentUser: () => void;
}) => {
  const { t } = useTranslation('cleanapi');

  const current = useCurrentTabNameFromUrl();

  const tabs: Tab[] = [
    { name: 'diagnostic', text: t('cleanapi.menu.diagnostic'), path: '' },
    { name: 'history', text: t('cleanapi.menu.history'), path: 'history' },
    { name: 'rules', text: t('cleanapi.menu.rules'), path: 'rules' },
  ];

  return (
    <div className="ippon-header">
      <div className="ippon-header--slot">
        <LogoComponent />
      </div>
      <IpponHSpaceSlot expand>
        <div className="ippon-header--slot">
          <div className="ippon-tabs" id="main-navigation" role="tablist">
            {tabs.map((tab) => (
              <TabComponent key={tab.name} tab={tab} current={current} />
            ))}
          </div>
        </div>
      </IpponHSpaceSlot>
      <div className="ippon-header--slot">
        <IpponHSpace gap={16} align="middle">
          <DiagnosticTrackingIndicatorComponent />
          <RuleExtractionTrackingIndicatorComponent />
          <UserMenuComponent
            currentUser={currentUser}
            onRefreshCurrentUser={onRefreshCurrentUser}
          />
        </IpponHSpace>
      </div>
    </div>
  );
};
